import React, { ChangeEvent, useContext, useEffect, useState } from "react";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import {
  FormControl,
  InputLabel,
  makeStyles,
  Select,
  Typography,
} from "@material-ui/core";
import { Theme } from "@material-ui/core/styles";
import CreateIcon from "@material-ui/icons/Create";

import LightPurpleContainer from "../../../components/LightPurpleContainer";
import Button from "../../../components/Button";
import ModalDialog from "../../../components/Form/ModalDialog";
import StatisticsForm, { isStatisticsBlank } from "./StatisticsForm";
import { getStatisticsForHospitalByYear } from "../../../api/statistics";
import { errorCodes } from "../../../constants/errors";
import { HospitalsContext } from "../RodaHomepage";
import { Statistics } from "../../../types/statistics";
import { blankStatistics } from "../../../constants/objects";
import NoData from "../../../components/NoData";
import { PublicHospitalsContext } from "../../PublicStatistics/PublicStatistics";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "flex",
    justifyContent: "space-between",
    [theme.breakpoints.only("xs")]: {
      flexDirection: "column",
    },
  },
  table: {
    backgroundColor: "#fff",
    borderRadius: 4,
  },
  head: {
    color: theme.palette.secondary.main,
    fontWeight: "bold",
    padding: theme.spacing(3, 0, 3, 6),
    [theme.breakpoints.only("xs")]: {
      display: "block",
    },
  },
  info: {
    width: "30%",
    fontWeight: "normal",
    [theme.breakpoints.only("xs")]: {
      display: "block",
      paddingLeft: theme.spacing(6),
      paddingRight: theme.spacing(6),
      width: "auto",
    },
  },
  editIcon: {
    fontSize: theme.spacing(2.2),
    color: theme.palette.primary.main,
  },
  title: {
    color: theme.palette.primary.main,
    display: "inline-block",
    fontSize: theme.spacing(2.5),
    fontWeight: "bold",
    margin: theme.spacing(1.8),
  },
  formControl: {
    marginTop: theme.spacing(2),
    minWidth: 140,
  },
  select: {
    borderRadius: 4,
    height: theme.spacing(6),
    "& .MuiSelect-select:focus": {
      backgroundColor: "rgba(0,0,0,0)",
    },
  },
  marginTop: {
    marginTop: theme.spacing(3),
  },
}));

type StatisticsTableProps = {
  isPublic?: boolean;
};

export default function StatisticsTable({ isPublic }: StatisticsTableProps) {
  const classes = useStyles();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [years, setYears] = useState<number[]>([]);
  const [year, setYear] = useState<number>(new Date().getFullYear() - 1);
  const [statisticsData, setStatisticsData] =
    useState<Statistics>(blankStatistics);
  const { currentHospital } = useContext(HospitalsContext);
  const { currentPublicHospital } = useContext(PublicHospitalsContext);

  useEffect(() => {
    async function getStatisticsForHospital() {
      let response;
      try {
        if (isPublic) {
          response = await getStatisticsForHospitalByYear(
            currentPublicHospital,
            year
          );
        } else {
          response = await getStatisticsForHospitalByYear(
            currentHospital,
            year
          );
        }
        setStatisticsData(response.data);
      } catch (error: any) {
        if (error.response && error.response.status === errorCodes.NOT_FOUND) {
          setStatisticsData({
            ...blankStatistics,
            year: year,
            hospitalId: currentHospital,
          });
        }
      }
    }

    function populateYears() {
      let startYear = 2016;
      let year = new Date().getFullYear();
      let yearsArray = [];

      for (year; year >= startYear; year--) {
        yearsArray.push(year);
      }

      setYears(yearsArray);
    }

    getStatisticsForHospital();
    populateYears();
  }, [currentHospital, currentPublicHospital, isPublic, year]);

  const handleYearChange = (event: ChangeEvent<{ value: unknown }>) => {
    setYear(event.target.value as number);
  };

  return (
    <>
      <div className={classes.root}>
        <FormControl className={classes.formControl}>
          <InputLabel id="year-select-label" variant="outlined">
            Odaberite godinu:
          </InputLabel>
          <Select
            native
            labelId="year-select-label"
            id="year-select"
            value={year}
            label="Odaberite godinu:"
            variant="outlined"
            onChange={handleYearChange}
            className={classes.select}
            disableUnderline
          >
            {years.map((year) => (
              <option value={year}>{year.toString()}</option>
            ))}
          </Select>
        </FormControl>
        {!isPublic && (
          <Button
            type="button"
            startIcon={<CreateIcon/>}
            onClick={() => setModalOpen(true)}
          >
            {isStatisticsBlank(statisticsData) ? "Unesi" : "Uredi"}
          </Button>
        )}
        {modalOpen && (
          <ModalDialog
            modalOpen={modalOpen}
            handleClose={() => setModalOpen(false)}
            maxWidth="lg"
            title={
              <Typography className={classes.title}>
                {isStatisticsBlank(statisticsData) ? "Unesi" : "Uredi"}
              </Typography>
            }
            icon={<CreateIcon className={classes.editIcon}/>}
          >
            <StatisticsForm
              isNew={isStatisticsBlank(statisticsData)}
              statisticsData={statisticsData}
              setStatisticsData={setStatisticsData}
              handleClose={() => setModalOpen(false)}
            />
          </ModalDialog>
        )}
      </div>
      {isStatisticsBlank(statisticsData) && (
        <NoData>
          Nema statističkih podataka za ovu godinu.
          <br/>
          {!isPublic && 'Kliknite na gumb "Unesi" kako biste unijeli podatke.'}
        </NoData>
      )}
      {!isStatisticsBlank(statisticsData) && (
        <LightPurpleContainer
          style={
            isPublic
              ? {
                paddingLeft: 0,
                paddingRight: 0,
                paddingTop: 0,
                marginTop: 16,
              }
              : {}
          }
          className={classes.marginTop}
        >
          <Table className={classes.table} aria-label="simple table">
            <TableBody>
              {statisticsData &&
                statisticsData.values.map((value) => (
                  <TableRow key={"question" + value.fieldId}>
                    <TableCell variant="head" className={classes.head}>
                      {value.fieldText}
                    </TableCell>
                    <TableCell
                      className={classes.info}
                      style={value.fieldId == 12 ? { paddingRight: 40 } : {}}
                    >
                      {value.valueText ? value.valueText : "Nema podataka"}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <Typography color="textSecondary" style={{ marginBottom: -12, marginTop: 12 }}>
            Udruga RODA odgovorna je za točnost prikazanih statističkih podataka.
          </Typography>
        </LightPurpleContainer>
      )}
    </>
  );
}
